import React from "react";
import type {
  MonthlyReportId,
  OperatorOverview,
  OperatorPipelineV1,
  OperatorRecapV1,
  OperatorReportRow,
  RecapPeriod,
  SeoAuditRunId,
  SiteId,
} from "@siteagain/shared-types";

import type { Locale } from "../../i18n";
import type { OperatorCopy } from "../copy";
import { formatSpend, formatMonth, relativeTime } from "../format";
import { SiteMark, siteInitials, siteTone } from "../SiteMark";
import { CHIP, averageScore, buildMovers, dailyCounts, inboxSubtitle, recapCounts, siteStage, sparkline, type InboxItem } from "./data";
import type { MC2Copy } from "./copy";
import { TopSearch, type MCView } from "./shell";

export function BriefPage({
  overview,
  pipeline,
  recap,
  reports,
  inbox,
  copy,
  ops,
  locale,
  now,
  period,
  onPeriod,
  sites,
  searchQuery,
  onSearch,
  onOpenSite,
  onOpenAudit,
  onOpenReport,
  onOpenInbox,
  onView,
  onAddSite,
  refreshSlot,
}: {
  overview: OperatorOverview;
  pipeline: OperatorPipelineV1 | null;
  recap: OperatorRecapV1 | null;
  reports: OperatorReportRow[] | null;
  inbox: InboxItem[];
  copy: MC2Copy;
  ops: OperatorCopy;
  locale: Locale;
  now: number;
  period: RecapPeriod;
  onPeriod(period: RecapPeriod): void;
  sites: readonly OperatorOverview["sites"][number][];
  searchQuery: string;
  onSearch(value: string): void;
  onOpenSite(siteId: SiteId): void;
  onOpenAudit(auditId: SeoAuditRunId): void;
  onOpenReport(reportId: MonthlyReportId): void;
  onOpenInbox(id: string): void;
  onView(view: MCView): void;
  onAddSite(): void;
  refreshSlot: React.ReactNode;
}) {
  const t = copy.brief;
  const counts = recapCounts(recap);
  const score = averageScore(overview.sites);
  const decisions = inbox.slice(0, 5);
  const movers = buildMovers(recap?.journal ?? []);
  const draftReports = overview.sites
    .flatMap((site) => (site.latestReport?.status === "draft" ? [{ site, report: site.latestReport }] : []));
  const running = overview.sites
    .filter((site) => site.activeWorkflows > 0 || (site.latestMigration && !["completed", "failed", "aborted"].includes(site.latestMigration.status)))
    .slice(0, 4);
  const stageRows = (["pending", "migrating", "preview_ready", "active", "failed"] as const).map((status) => ({
    label: siteStage({ status } as OperatorOverview["sites"][number], locale).label,
    count: overview.sites.filter((site) => site.status === status).length,
  }));
  const stageMax = Math.max(1, ...stageRows.map((stage) => stage.count));
  const deployedSpark = sparkline(dailyCounts(recap?.journal ?? [], now, 7, ["deployed"]));
  const auditSpark = sparkline(dailyCounts(recap?.journal ?? [], now, 7, ["audit_completed"]));
  const waitingSpark = sparkline(dailyCounts(recap?.journal ?? [], now, 7, ["preview_ready"]));

  const nextPeriod = () => {
    onPeriod(period === "24h" ? "7d" : period === "7d" ? "30d" : "24h");
  };
  const periodLabel = period === "24h" ? (locale === "fr" ? "24 dernières heures" : "Last 24 hours") : period === "7d" ? (locale === "fr" ? "7 derniers jours" : "Last 7 days") : (locale === "fr" ? "30 derniers jours" : "Last 30 days");

  return (
    <>
      <div className="mc2-topbar">
        <TopSearch sites={sites} placeholder={copy.fleet.search} ariaLabel={copy.fleet.search} onOpenSite={onOpenSite} value={searchQuery} onChange={onSearch} />
        <div className="mc2-topactions">
          <div className="mc2-weekpick">
            <button type="button" className="mc2-iconbtn" aria-label="Previous period" onClick={() => onPeriod(period === "30d" ? "7d" : period === "7d" ? "24h" : "30d")}>‹</button>
            <button type="button" className="mc2-weeklbl" onClick={nextPeriod}>{periodLabel}</button>
            <button type="button" className="mc2-iconbtn" aria-label="Next period" onClick={nextPeriod}>›</button>
          </div>
          {refreshSlot}
          <button type="button" className="mc2-btn" onClick={onAddSite}>+ {copy.add.step1 === "Site" ? "" : ""}{locale === "fr" ? "Ajouter un site" : "Add website"}</button>
        </div>
      </div>

      <div className="mc2-brief">
        <div className="mc2-brief-head">
          <div className="mc2-eyebrow">{t.eyebrow} · {new Date(now).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-GB", { weekday: "long", day: "numeric", month: "long" })}</div>
          <h1>{t.headline(counts.deployed, inbox.length, counts.improved)}</h1>
          <p>{t.lead(overview.fleet.attentionSites, counts.audits, draftReports.length)}</p>
        </div>

        <div className="mc2-kpis">
          <Kpi label={t.kpi.shipped} value={String(counts.deployed)} note={t.kpiNote.shipped} spark={deployedSpark} />
          <Kpi label={t.kpi.score} value={score.value === null ? "—" : score.value.toFixed(1)} note={t.kpiNote.score(score.count)} />
          <Kpi label={t.kpi.waiting} value={String(overview.fleet.awaitingApproval)} note={t.kpiNote.waiting} spark={waitingSpark} />
          <Kpi label={t.kpi.audits} value={String(counts.audits)} note={t.kpiNote.audits} spark={auditSpark} />
          <Kpi label={t.kpi.spend} value={formatSpend(overview.fleet.monthUsageMicroUsd)} note={t.kpiNote.spend} />
        </div>

        <div className="mc2-cols">
          <div className="mc2-col-main">
            <section className="mc2-card" aria-labelledby="mc2-decisions">
              <div className="mc2-cardhead">
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <h2 id="mc2-decisions">{t.decisions}</h2>
                  <span className={CHIP.ink}>{inbox.length}</span>
                  <span className="mc2-muted" style={{ fontSize: 13 }}>{t.decisionsHint}</span>
                </div>
                <button type="button" className="mc2-btn ghost sm" onClick={() => onView("inbox")}>{t.openInbox} <span className="mc2-kbd">G I</span></button>
              </div>
              {decisions.length === 0 ? (
                <p className="mc2-empty">{copy.inbox.clear}</p>
              ) : decisions.map((item) => (
                <DecisionRow
                  key={item.id}
                  item={item}
                  ops={ops}
                  locale={locale}
                  now={now}
                  copy={copy}
                  onOpen={() => {
                    if (item.kind === "audit") onOpenAudit(item.auditId);
                    else if (item.kind === "report") onOpenReport(item.report.id);
                    else onOpenInbox(item.id);
                  }}
                />
              ))}
            </section>

            <section className="mc2-card" aria-labelledby="mc2-moved">
              <div className="mc2-cardhead">
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <h2 id="mc2-moved">{t.moved}</h2>
                  <span className="mc2-muted" style={{ fontSize: 13 }}>{t.movedHint}</span>
                </div>
                <button type="button" className="mc2-linkbtn" style={{ border: 0, background: "transparent", cursor: "pointer" }} onClick={() => onView("fleet")}>{t.allSites} →</button>
              </div>
              {movers.length === 0 ? (
                <p className="mc2-empty">{t.movedEmpty}</p>
              ) : movers.map((mover) => (
                <div className="mc2-mover" key={mover.siteId + mover.at}>
                  <button type="button" className="site" style={{ border: 0, background: "transparent", padding: 0, cursor: "pointer", textAlign: "left" }} onClick={() => onOpenSite(mover.siteId)}>
                    <SiteMark siteId={mover.siteId} name={mover.siteName} />
                    <span>{mover.siteName}</span>
                  </button>
                  <span className={`delta mc2-${mover.tone}`}>{mover.mark}</span>
                  <span className="why">{mover.why}</span>
                </div>
              ))}
            </section>
          </div>

          <div className="mc2-col-side">
            <section className="mc2-card" aria-labelledby="mc2-stages">
              <div className="mc2-cardhead">
                <h2 id="mc2-stages">{t.stages}</h2>
                <button type="button" className="mc2-linkbtn" style={{ border: 0, background: "transparent", cursor: "pointer" }} onClick={() => onView("pipeline")}>{t.pipeline} →</button>
              </div>
              <div style={{ padding: "14px 20px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
                {overview.sites.length === 0 ? <p className="mc2-muted" style={{ margin: 0, fontSize: 13 }}>{t.noSites}</p> : stageRows.map((stage) => (
                  <div className="mc2-stage-row" key={stage.label}>
                    <span style={{ fontSize: 13 }}>{stage.label}</span>
                    <div className="mc2-bar"><div className="mc2-barfill" style={{ width: stage.count === 0 ? "0%" : `${Math.max(6, (stage.count / stageMax) * 100)}%` }} /></div>
                    <span className="mc2-mono">{stage.count}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="mc2-card" aria-labelledby="mc2-updates">
              <div className="mc2-cardhead">
                <h2 id="mc2-updates">{t.updates}</h2>
                <span className={CHIP.lime}>{t.updatesReady(draftReports.length)}</span>
              </div>
              <p className="mc2-muted" style={{ margin: 0, padding: "14px 20px 4px", fontSize: 13 }}>{t.updatesBody}</p>
              {draftReports.length === 0 ? (
                <p className="mc2-empty">{t.noDrafts}</p>
              ) : draftReports.slice(0, 5).map(({ site, report }) => (
                <div className="mc2-li" key={report.id}>
                  <span className="mc2-av" style={{ background: siteTone(site.id), width: 24, height: 24, fontSize: 10 }}>{siteInitials(site.name)}</span>
                  <button type="button" style={{ flex: 1, minWidth: 0, border: 0, background: "transparent", textAlign: "left", padding: 0, cursor: "pointer", color: "inherit" }} onClick={() => onOpenReport(report.id)}>
                    {site.name}
                  </button>
                  <span className={CHIP.warn}>{formatMonth(report.month, locale)}</span>
                </div>
              ))}
              <div style={{ padding: "14px 20px 18px" }}>
                <button type="button" className="mc2-btn" style={{ width: "100%" }} onClick={() => onView("reports")}>{t.reviewSend(draftReports.length)}</button>
              </div>
            </section>

            <section className="mc2-card" aria-labelledby="mc2-running">
              <div className="mc2-cardhead">
                <h2 id="mc2-running">{t.running}</h2>
                <span className="mc2-muted" style={{ fontSize: 13 }}>{t.runningHint}</span>
              </div>
              {running.length === 0 ? (
                <p className="mc2-empty">{t.runningEmpty}</p>
              ) : running.map((site) => {
                const migration = site.latestMigration;
                const pct = migration?.progressPercent ?? null;
                return (
                  <div className="mc2-running" key={site.id}>
                    <div className="top">
                      <span><strong>{migration ? t.migration : t.work}</strong> · {site.name}</span>
                      <span className="mc2-mono mc2-muted">{pct === null ? t.active(site.activeWorkflows) : `${Math.round(pct)}%`}</span>
                    </div>
                    <div className="mc2-bar">
                      <div className={`mc2-barfill${pct === null ? " indeterminate" : ""}`} style={pct === null ? undefined : { width: `${Math.max(6, Math.min(100, pct))}%` }} />
                    </div>
                  </div>
                );
              })}
              <div style={{ padding: "12px 20px 16px", fontSize: 13 }} className="mc2-muted">
                {counts.measured > 0 ? t.measuring(counts.measured) : copy.brief.runningEmpty}
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}

function Kpi({ label, value, note, spark }: { label: string; value: string; note: string; spark?: string | null }) {
  return (
    <div className="mc2-card mc2-kpi">
      <div className="mc2-eyebrow">{label}</div>
      <div className="krow">
        <div className="kval">{value}</div>
        {spark && (
          <svg width="84" height="28" viewBox="0 0 84 28" aria-hidden="true">
            <polyline points={spark} fill="none" stroke="var(--ink)" strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
          </svg>
        )}
      </div>
      <div className="note"><span className="mc2-muted">{note}</span></div>
    </div>
  );
}

function DecisionRow({ item, copy, ops, locale, now, onOpen }: { item: InboxItem; copy: MC2Copy; ops: OperatorCopy; locale: Locale; now: number; onOpen(): void }) {
  const meta = kindMeta(item.kind, copy);
  return (
    <div className="mc2-decide">
      <span className={`${CHIP[meta.tone]} kind`}>{meta.label}</span>
      <div className="body">
        <div className="title">{item.title}</div>
        <div className="detail">{inboxSubtitle(item, copy, ops)} · {relativeTime(item.at, locale, now)}</div>
      </div>
      <button type="button" className="mc2-btn sm" onClick={onOpen}>{copy.inbox.review}</button>
    </div>
  );
}

function kindMeta(kind: InboxItem["kind"], copy: MC2Copy): { label: string; tone: keyof typeof CHIP } {
  switch (kind) {
    case "stopped": return { label: copy.inbox.kind.stopped, tone: "down" };
    case "approve": return { label: copy.inbox.kind.approve, tone: "ink" };
    case "audit": return { label: copy.inbox.kind.audit, tone: "info" };
    case "report": return { label: copy.inbox.kind.report, tone: "lime" };
    case "setup": return { label: copy.inbox.kind.setup, tone: "warn" };
    case "incident": return { label: copy.inbox.kind.incident, tone: "down" };
    default: return { label: kind, tone: "neutral" };
  }
}
